<template>
  <div class="scroll-wrapper__wrapper">
    <div ref="contentWrapper" class="scroll-wrapper__content-wrapper">
      <slot
        :resize="getContentSizeWithScroll"
        :resize-without-scroll="getContentSizeWithoutScroll"
      />
    </div>
    <client-only>
      <a-visibility v-if="isScrollVisible" hide :on="[$breakpoint.mobile]">
        <vue-perfect-scrollbar
          ref="scroll"
          class="scroll-wrapper__scrollbar"
          @scroll="onScroll"
          @ps-scroll-x="onScroll"
        >
          <!--
            This hidden container is needed for perfect-scrollbar to calculate
            correct scrollbar size.
            The dimensions of this container are updated according to real slot
            dimensions.
           -->
          <div
            :style="scrollbarContentDynamicStyle"
            class="scroll-wrapper__scrollbar-content"
          />
        </vue-perfect-scrollbar>
      </a-visibility>
    </client-only>
  </div>
</template>

<script>
import { propValidator, PROP_TYPES } from '@/utils/validators'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { KEY_CODE } from '@fmpedia/enums'

export default {
  name: 'HorizontalScrollWrapper',
  mixins: [hydrationHelpers],
  props: {
    isLoaded: propValidator([PROP_TYPES.BOOLEAN], false, true),
    centered: propValidator([PROP_TYPES.BOOLEAN], false, true)
  },
  data() {
    return {
      contentWidth: 0,
      contentHeight: 0
    }
  },
  computed: {
    isScrollVisible() {
      if (
        this.$_hydrationHelpers_isLayoutMobile ||
        !this.contentWidth ||
        !this.contentHeight
      )
        return false

      const containerWidth = this.$el.clientWidth
      return this.contentWidth > containerWidth
    },
    scrollbarContentDynamicStyle() {
      return {
        width: `${this.contentWidth}px`,
        height: `${this.contentHeight}px`
      }
    }
  },
  watch: {
    isLoaded: {
      immediate: true,
      handler(newVal) {
        if (process.client && newVal) {
          this.getContentSizeWithScroll()
        }
      }
    }
  },
  methods: {
    getContentSize() {
      this.$nextTick(() => {
        try {
          if (!this.$refs.contentWrapper) return

          const content = this.$refs.contentWrapper.children[0]
          this.contentWidth = content.clientWidth
          this.contentHeight = content.clientHeight
        } catch (err) {
          this.$errorHandler(err, this, {
            showMessage: false,
            message: 'Cannot get content for horizontal scrolling'
          })
        }
      })
    },
    async getContentSizeWithScroll() {
      await this.$nextTick(this.getContentSize)

      await this.$nextTick()
      if (this.centered && this.isScrollVisible) {
        this.setScrollToCenter()
      }
    },
    getContentSizeWithoutScroll() {
      this.$nextTick(this.getContentSize)
    },
    setScrollToCenter() {
      if (!this.isScrollVisible) return

      const scroll = this.$refs.scroll.$el
      const scrollWidth = scroll.clientWidth

      const scrollValue = Math.round((this.contentWidth - scrollWidth) / 2)
      scroll.scroll(scrollValue, 0)
      this.onScroll()
    },
    /**
     * When the user tabs over focusable elements, the container scrolls,
     * and we update the horizontal scroll position here
     */
    async onDocumentKeyDown(event) {
      if (!this.isScrollVisible || event.keyCode !== KEY_CODE.TAB) {
        return
      }

      setTimeout(() => {
        const scroll = this.$refs.scroll.$el
        const contentWrapper = this.$refs.contentWrapper

        const containerScrollLeft = contentWrapper.scrollLeft
        const scalingFactor = this.getScrollScalingFactor()
        const newScrollLeft = containerScrollLeft / scalingFactor

        scroll.scroll(Math.ceil(newScrollLeft), 0)
      })
    },
    onScroll() {
      const contentWrapper = this.$refs.contentWrapper
      const scroll = this.$refs.scroll.$el

      const sl = scroll.scrollLeft
      const k = this.getScrollScalingFactor()

      contentWrapper.scroll(Math.ceil(sl * k), 0)
    },
    onMouseScroll(e) {
      if (e.shiftKey) {
        if (!this.isScrollVisible || !this.$refs.scroll) {
          return
        }
        const scroll = this.$refs.scroll.$el
        const scrollAmount = e.deltaX || e.deltaY
        const k = this.getScrollScalingFactor()
        const sl = scroll.scrollLeft + scrollAmount / k
        scroll.scroll(Math.ceil(sl), 0)

        this.onScroll()
      }
    },
    getScrollScalingFactor() {
      const scroll = this.$refs.scroll.$el
      const contentWrapper = this.$refs.contentWrapper
      const contentWrapperWidth = contentWrapper.clientWidth
      const scrollWidth = scroll.clientWidth

      return (
        (this.contentWidth - contentWrapperWidth) /
        (this.contentWidth - scrollWidth)
      )
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onDocumentKeyDown)
    this.$refs.contentWrapper.addEventListener('wheel', this.onMouseScroll)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onDocumentKeyDown)
    this.$refs.contentWrapper.removeEventListener('wheel', this.onMouseScroll)
  }
}
</script>

<style lang="scss">
/* TODO requires Safari fix */
.scroll-wrapper__wrapper {
  /*display: flex;*/
  /*flex-direction: column;*/
  width: 100%;

  .scroll-wrapper__content-wrapper {
    width: 100%;
    height: 100%;
    overflow: hidden;
    /*flex: 1 1 auto;*/
    /*display: flex;*/

    & > * {
      /*flex: 1 1 auto;*/
      width: max-content; /* Should be fixed for Internet Explorer */
      margin: 0 auto;
    }

    @include mobile {
      height: auto;

      & > * {
        width: 100%;
      }
    }
  }

  .scroll-wrapper__scrollbar {
    width: 60%;
    height: 15px;
    margin: 0 auto;
    overflow-x: scroll;
    border-radius: 5px;

    .ps__scrollbar-x-rail {
      background: #eee;
      opacity: 1;
    }

    .ps__scrollbar-x {
      height: 11px;
    }
  }

  .scroll-wrapper__scrollbar-content {
    width: max-content;
    visibility: hidden;
    content: '';

    @include mobile {
      height: auto;
    }
  }
}
</style>
